import fetchPostDetails from '@dehaat/kisan-app-bl/api/fetchPostDetailsV2';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { UserPosts } from '@dehaat/kisan-app-bl/models/PostsV2';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import BottomDrawer from '@/components/BottomDrawer';
import ContentLDSchema from '@/components/ContentLDSchema';
import CropDoctorHelp from '@/components/CropDoctorHelp';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import BellIcon from '@/components/icons/BellIcon';
import DownloadAppIcon from '@/components/icons/DownloadApp';
import VideoCallIcon from '@/components/icons/VideoCallIcon';
import BannerWithCTAV2 from '@/components/posts/BannerWithCTAV2';
import CommentSectionModal from '@/components/posts/CommentsSectionModal';
import DehaatFeedCard from '@/components/posts/DehaatFeedCard';
import RelatedPosts from '@/components/posts/RelatedPosts';
import SocialMetaTags from '@/components/SocialMetaTags';
import { ACCESS_TOKEN_KEY, APP_LINK, FeedTabs, VIDEO_CALL_LINK, WEBSITE_URL } from '@/constants/common';
import { POST_TYPE } from '@/constants/posts';
import { popUpBannerDelay, POST_TYPE as POST_TYPEV2 } from '@/constants/postv2';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import DownloadBannerV2 from '@/images/posts/download-app-bannerv2.webp';
import NotificationBanner from '@/images/posts/notification-banner.webp';
import VideoCallBanner from '@/images/posts/video-call-banner.webp';
import { getThumbnailUrl, htmlToString } from '@/utils/helper';
import { getRandomValueWithProbabilities } from '@/utils/helpers/getRandomValueFromArray';
import { askNotificationPermission, isNotificationPermissionAvailable } from '@/utils/helpers/promptNotifications';
export const BannersList = [{
  image: DownloadBannerV2,
  ctaIcon: <>
        <DownloadAppIcon className="fill-transparent stroke-1 stroke-white mx-3 w-6 h-6" />
      </>,
  ctaLabel: 'download_app',
  ctaLink: APP_LINK
}, {
  image: VideoCallBanner,
  ctaIcon: <>
        <VideoCallIcon className="fill-transparent stroke-1 stroke-white mx-3 w-6 h-6" />
      </>,
  ctaLabel: 'video_call',
  ctaLink: VIDEO_CALL_LINK
}

//Other options such as allow notifications etc can be added here
];
export interface Props {
  postDetails: UserPosts;
}
const DehaatPostPage = ({
  postDetails
}: Props) => {
  const {
    query: {
      slug,
      c
    },
    locale
  } = useRouter();
  const {
    t
  } = useTranslation('posts');
  const [bannerData, setBannerData] = useState(BannersList[0]);
  const pageUrl = `${WEBSITE_URL}${postDetails.language === DEFAULT_LANG ? '' : `/${postDetails.language}`}/blog/${postDetails.slug}`;
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(Boolean(c));
  const [commentCount, setCommentCount] = useState(postDetails?.comment_count || 0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerOpenOnce, setIsDrawerOpenOnce] = useState(false);
  const postDescription = htmlToString(postDetails?.content || '');
  const reducedDescription = postDescription.substring(0, 300);
  const [href, setHref] = useState('');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const timer = useRef<number>();
  const handleAddComment = () => {
    setCommentCount(prev => prev + 1);
  };
  const toggleDrawer = () => {
    setIsDrawerOpen(prev => !prev);
  };
  useEffect(() => {
    timer.current = window.setTimeout(() => {
      setIsDrawerOpen(true);
      setIsDrawerOpenOnce(true);
    }, popUpBannerDelay);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);
  useEffect(() => {
    setHref(`${location.origin}/blog/${slug}`);
  }, [slug]);
  useEffect(() => {
    const notificationObj = {
      image: NotificationBanner,
      ctaIcon: <>
          <BellIcon className="fill-transparent stroke-1 stroke-white mx-3 w-6 h-6" />
        </>,
      ctaLabel: 'allow_notification',
      onClick: async () => {
        await askNotificationPermission(trackCustomEvent);
        toggleDrawer();
      },
      ctaLink: ''
    };
    if (!isNotificationPermissionAvailable()) {
      BannersList.push(notificationObj);
    }
  }, []);
  useEffect(() => {
    if (BannersList.length > 1) {
      const dataForBanner = getRandomValueWithProbabilities(BannersList, !isNotificationPermissionAvailable() ? [0.25, 0.25, 0.5] : [0.5, 0.5] // Configurable probability of each option, for eg:- [0.2, 0.5, 0.3] for three options
      );
      setBannerData(dataForBanner);
    }
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.innerHeight + window.scrollY;
      const isBottom = scrolled >= document.documentElement.scrollHeight - 100;
      if (isBottom && !hasScrolledToBottom) {
        window.removeEventListener('scroll', handleScroll);
        setHasScrolledToBottom(true);
        if (timer.current && !isDrawerOpenOnce) {
          setIsDrawerOpen(true);
          clearTimeout(timer.current);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolledToBottom, isDrawerOpenOnce]);
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="[slug].tsx">
        <title>{postDetails.name}</title>
        <meta name="title" content={postDetails.name as string} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta name="description" content={reducedDescription} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" hrefLang={`${postDetails.language}-in`} href={pageUrl} />
      </Head>
      <SocialMetaTags image={(postDetails.attachments?.images || [])[0]} video={(postDetails.attachments?.video_links || [])[0]} title={postDetails.name as string} description={reducedDescription} url={`${locale}/blog/${slug}`} data-sentry-element="SocialMetaTags" data-sentry-source-file="[slug].tsx" />
      <ContentLDSchema contentUrl={`/blog/${postDetails.slug}`} createdAt={postDetails.created_at} description={postDescription} imagesList={(postDetails.attachments?.video_links || []).length > 0 ? postDetails.attachments.video_links.map(getThumbnailUrl) : postDetails.attachments?.images} name={postDetails.name as string} updatedAt={postDetails.updated_at} userFullName={postDetails.head?.name || ''} videoLink={(postDetails.attachments?.video_links || [])[0]} data-sentry-element="ContentLDSchema" data-sentry-source-file="[slug].tsx" />
      <main className="bg-neutral-10 min-h-screen">
        <HeaderWithBackButton homePath={`/?tab=${FeedTabs.COMMUNITY}`} heading={showCommentModal ? t('total_comments_caption') : t('details_caption')} showHomeIcon={false} showProfileIcon={false} showWhatsAppIcon={!showCommentModal} whatsappShareMessage={href} onBackClick={showCommentModal && !c ? () => setShowCommentModal(false) : undefined} data-sentry-element="HeaderWithBackButton" data-sentry-source-file="[slug].tsx" />
        {showCommentModal ? <CommentSectionModal addComment={handleAddComment} postId={postDetails.id} postType={POST_TYPE.Blog} postCreator={postDetails?.head?.name} tags={postDetails?.tags || []} /> : <>
            <section className="bg-white mx-auto pt-16 max-w-7xl">
              <div id="header-ad" className="flex justify-center items-center h-[1px]" />
              <DehaatFeedCard commentCount={commentCount} {...postDetails} onCommentClick={() => setShowCommentModal(true)} />
            </section>

            <section className="m-auto px-4 pt-6 max-w-7xl">
              <RelatedPosts slug={slug as string} postType="dehaat" />
            </section>
            <section className="bg-white mx-auto mt-2 p-2 max-w-7xl">
              <CropDoctorHelp />
            </section>
            <BottomDrawer show={isDrawerOpen} onClose={toggleDrawer}>
              <div className="bg-white p-3">
                <BannerWithCTAV2 {...bannerData} />
              </div>
            </BottomDrawer>
          </>}
        <div id="footer-ad-banner" className="flex justify-center items-center h-[1px]" />
      </main>
    </>;
};
export async function getServerSideProps({
  req: {
    cookies
  },
  query,
  locale
}: GetServerSidePropsContext) {
  const token = cookies[ACCESS_TOKEN_KEY];
  const postDetailsResponse = await fetchPostDetails(query.slug as string, POST_TYPEV2.Blog, locale, token);
  if (postDetailsResponse) {
    return {
      props: {
        postDetails: postDetailsResponse
      }
    };
  }
  return {
    notFound: true
  };
}
export default DehaatPostPage;